import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { motion } from "framer-motion";
import { FileText, ArrowLeft, Copy, Check, Download, Sparkles, Lock } from "lucide-react";

const TEMPLATES = [
  {
    id: "modern",
    label: "Modern",
    description: "Clean serif headings, generous spacing",
    nameStyle: { fontFamily: "Georgia, serif", fontSize: 22, fontWeight: 700, letterSpacing: "-0.01em" },
    headerBg: "transparent",
    headerColor: "var(--rolex-green)",
    align: "left",
  },
  {
    id: "classic",
    label: "Classic",
    description: "Traditional letterhead with rule below name",
    nameStyle: { fontFamily: "Georgia, serif", fontSize: 24, fontWeight: 700, paddingBottom: 6, borderBottom: "1.5px solid #006039" },
    headerBg: "transparent",
    headerColor: "#1A1A18",
    align: "left",
  },
  {
    id: "creative",
    label: "Creative",
    description: "Bold green accent block",
    nameStyle: { fontFamily: "Georgia, serif", fontSize: 22, fontWeight: 700, color: "white" },
    headerBg: "var(--rolex-green)",
    headerColor: "white",
    align: "left",
  },
];

const LENGTHS = [
  { id: "concise", label: "Concise", w: "~200w" },
  { id: "standard", label: "Standard", w: "~350w" },
  { id: "detailed", label: "Detailed", w: "~500w" },
];

export default function CoverLetterPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPaid = user?.plan === "pro" || user?.plan === "teams";
  const [jobTitle, setJobTitle] = useState("");
  const [company, setCompany] = useState("");
  const [jobDesc, setJobDesc] = useState("");
  const [background, setBackground] = useState("");
  const [name, setName] = useState(user?.name || "");
  const [tone, setTone] = useState("professional");
  const [length, setLength] = useState("standard");
  const [template, setTemplate] = useState("modern");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [editedLetter, setEditedLetter] = useState("");
  const [improving, setImproving] = useState(null);

  if (!user) return null;

  const activeTemplate = TEMPLATES.find((t) => t.id === template) || TEMPLATES[0];
  const letterText = editedLetter || result?.cover_letter || "";

  const handleGenerate = async () => {
    if (!jobTitle || !company || !jobDesc || !background) return;
    setLoading(true); setResult(null); setEditedLetter("");
    try {
      const { data } = await API.post("/tools/cover-letter", { job_title: jobTitle, company_name: company, job_description: jobDesc, background, tone, length, name });
      setResult(data);
      setEditedLetter(data.cover_letter || "");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "Something went wrong.");
      setResult({ error: true, message: msg });
    } finally { setLoading(false); }
  };

  const handleCopy = () => {
    try { navigator.clipboard.writeText(letterText); } catch {}
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  const handleImproveParagraph = async (idx) => {
    if (!isPaid) { navigate("/pricing"); return; }
    const paragraphs = letterText.split(/\n\n+/);
    const para = paragraphs[idx];
    if (!para?.trim()) return;
    setImproving(idx);
    try {
      const { data } = await API.post("/tools/cover-letter/improve", { paragraph: para, context: `${jobTitle} at ${company}`, tone });
      paragraphs[idx] = data.improved;
      setEditedLetter(paragraphs.join("\n\n"));
    } catch (err) {
      const detail = err.response?.data?.detail;
      alert(typeof detail === "string" ? detail : "Could not improve paragraph.");
    } finally { setImproving(null); }
  };

  const downloadTxt = () => {
    const blob = new Blob([letterText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `cover-letter-${company.toLowerCase().replace(/\s+/g, "-")}.txt`; a.click();
  };

  // Download as printable PDF using browser print (window with HTML doc)
  const downloadPdf = () => {
    const html = buildPrintableHtml();
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(html); w.document.close();
    setTimeout(() => { w.print(); }, 400);
  };

  // Download as .doc (HTML wrapped as doc — opens in Word)
  const downloadDocx = () => {
    const html = buildPrintableHtml();
    const blob = new Blob(["\ufeff", html], { type: "application/msword" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `cover-letter-${company.toLowerCase().replace(/\s+/g, "-")}.doc`; a.click();
  };

  const buildPrintableHtml = () => {
    const safe = (s) => (s || "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" })[c]);
    const paragraphs = letterText.split(/\n\n+/).map(p => `<p style="margin:0 0 14px;line-height:1.65;">${safe(p).replace(/\n/g, "<br>")}</p>`).join("");
    const headerBlock = activeTemplate.id === "creative"
      ? `<div style="background:#006039;color:white;padding:28px 36px;margin-bottom:24px;"><h1 style="margin:0;font-family:Georgia,serif;font-size:26px;">${safe(name || "Your Name")}</h1><p style="margin:6px 0 0;font-size:13px;opacity:0.85;">Cover Letter — ${safe(company)}</p></div>`
      : activeTemplate.id === "classic"
        ? `<h1 style="margin:0 0 4px;font-family:Georgia,serif;font-size:28px;border-bottom:1.5px solid #006039;padding-bottom:6px;display:inline-block;">${safe(name || "Your Name")}</h1><p style="margin:8px 0 22px;font-size:13px;color:#666;">${safe(jobTitle)} — ${safe(company)}</p>`
        : `<h1 style="margin:0 0 4px;font-family:Georgia,serif;font-size:26px;color:#006039;letter-spacing:-0.01em;">${safe(name || "Your Name")}</h1><p style="margin:6px 0 22px;font-size:13px;color:#666;">Cover Letter for ${safe(jobTitle)} at ${safe(company)}</p>`;
    return `<!doctype html><html><head><meta charset="utf-8"><title>Cover Letter — ${safe(company)}</title><style>body{font-family:Georgia,'Times New Roman',serif;color:#1A1A18;max-width:680px;margin:40px auto;padding:0 32px;font-size:14px;}@page{margin:0.7in;}</style></head><body>${headerBlock}${paragraphs}</body></html>`;
  };

  const paragraphs = letterText ? letterText.split(/\n\n+/) : [];

  return (
    <div className="min-h-screen" style={{ background: "var(--surface-cream)" }}>
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 pt-6 pb-16">
        <button onClick={() => navigate("/ai-tools")} className="flex items-center gap-1 text-sm mb-4 hover:underline" style={{ color: "var(--rolex-green)" }} data-testid="back-to-tools"><ArrowLeft size={14} /> AI Tools</button>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--rolex-green-light)" }}><FileText size={20} style={{ color: "var(--rolex-green)" }} /></div>
          <div>
            <h1 className="font-display text-2xl font-bold" style={{ color: "var(--text-primary)" }}>Cover Letter Builder</h1>
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>Templates · live preview · paragraph-level AI improve · PDF / Word download</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* INPUT */}
          <div className="space-y-4">
            <div className="rounded-2xl p-5" style={{ background: "rgba(255,255,255,0.65)", backdropFilter: "blur(12px)", border: "1px solid rgba(232,228,220,0.5)" }}>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div><label className="label-caps block mb-1">Job Title *</label><input className="buddy-input text-sm py-2" placeholder="e.g. Product Manager" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} data-testid="cl-job-title" /></div>
                <div><label className="label-caps block mb-1">Company *</label><input className="buddy-input text-sm py-2" placeholder="e.g. Google" value={company} onChange={(e) => setCompany(e.target.value)} data-testid="cl-company" /></div>
              </div>
              <div className="mb-3"><label className="label-caps block mb-1">Job Description *</label><textarea className="buddy-input resize-none text-sm" rows={4} placeholder="Paste the job description…" value={jobDesc} onChange={(e) => setJobDesc(e.target.value)} data-testid="cl-job-desc" /></div>
              <div className="mb-3"><label className="label-caps block mb-1">Your Background *</label><textarea className="buddy-input resize-none text-sm" rows={3} placeholder="Your relevant skills, experience, achievements…" value={background} onChange={(e) => setBackground(e.target.value)} data-testid="cl-background" /></div>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div><label className="label-caps block mb-1">Your Name</label><input className="buddy-input text-sm py-2" placeholder="For sign-off" value={name} onChange={(e) => setName(e.target.value)} /></div>
                <div><label className="label-caps block mb-1">Tone</label><select className="buddy-input text-sm py-2" value={tone} onChange={(e) => setTone(e.target.value)} data-testid="cl-tone-select"><option value="professional">Professional</option><option value="confident">Confident</option><option value="casual">Casual</option><option value="enthusiastic">Enthusiastic</option></select></div>
              </div>
              <div className="mb-3">
                <label className="label-caps block mb-1.5">Length</label>
                <div className="flex gap-2" data-testid="cl-length-group">
                  {LENGTHS.map((l) => (
                    <button key={l.id} onClick={() => setLength(l.id)} className="flex-1 text-xs py-2 rounded-lg font-medium" style={length === l.id ? { background: "var(--rolex-green)", color: "white" } : { background: "var(--surface-warm)", color: "var(--text-muted)" }} data-testid={`cl-length-${l.id}`}>{l.label} <span className="opacity-70">{l.w}</span></button>
                  ))}
                </div>
              </div>
              <div className="mb-3">
                <label className="label-caps block mb-1.5">Template</label>
                <div className="flex gap-2" data-testid="cl-template-group">
                  {TEMPLATES.map((t) => (
                    <button key={t.id} onClick={() => setTemplate(t.id)} className="flex-1 p-2 rounded-lg text-left" style={template === t.id ? { background: "var(--rolex-green-light)", border: "1.5px solid var(--rolex-green)" } : { background: "var(--surface-warm)", border: "1.5px solid transparent" }} data-testid={`cl-template-${t.id}`}>
                      <p className="text-[11px] font-bold" style={{ color: template === t.id ? "var(--rolex-green)" : "var(--text-primary)" }}>{t.label}</p>
                      <p className="text-[9px]" style={{ color: "var(--text-muted)" }}>{t.description}</p>
                    </button>
                  ))}
                </div>
              </div>
              <motion.button onClick={handleGenerate} disabled={!jobTitle || !company || !jobDesc || !background || loading} className="btn-primary w-full py-3 text-sm" style={{ opacity: !jobTitle || !company || !jobDesc || !background ? 0.5 : 1 }} whileHover={{ scale: 1.01 }} data-testid="cl-generate">
                {loading ? <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto block" /> : "Generate Cover Letter"}
              </motion.button>
            </div>
          </div>

          {/* PREVIEW */}
          <div className="rounded-2xl p-6" style={{ background: "rgba(255,255,255,0.65)", backdropFilter: "blur(12px)", border: "1px solid rgba(232,228,220,0.5)", minHeight: "500px" }}>
            <div className="flex items-center justify-between mb-3">
              <span className="label-caps">Live Preview</span>
              {letterText && (
                <div className="flex items-center gap-2">
                  <button onClick={handleCopy} className="text-xs flex items-center gap-1" style={{ color: copied ? "var(--success)" : "var(--rolex-green)" }} data-testid="cl-copy">{copied ? <Check size={12} /> : <Copy size={12} />}{copied ? "Copied" : "Copy"}</button>
                  <button onClick={downloadTxt} className="text-xs flex items-center gap-1 px-2 py-1 rounded-md" style={{ background: "var(--surface-warm)", color: "var(--text-muted)" }} data-testid="cl-download-txt">.txt</button>
                  <button onClick={downloadDocx} className="text-xs flex items-center gap-1 px-2 py-1 rounded-md" style={{ background: "var(--info-bg)", color: "var(--info)" }} data-testid="cl-download-docx">.doc</button>
                  <button onClick={downloadPdf} className="text-xs flex items-center gap-1 px-2 py-1 rounded-md" style={{ background: "var(--rolex-green-light)", color: "var(--rolex-green)" }} data-testid="cl-download-pdf"><Download size={11} /> PDF</button>
                </div>
              )}
            </div>
            {loading ? (
              <div className="space-y-3"><div className="h-4 rounded animate-shimmer" style={{ width: "90%" }} /><div className="h-4 rounded animate-shimmer" style={{ width: "80%" }} /><div className="h-4 rounded animate-shimmer" style={{ width: "70%" }} /><div className="h-4 rounded animate-shimmer" style={{ width: "85%" }} /></div>
            ) : letterText ? (
              <div className="rounded-xl overflow-hidden" style={{ background: "white", border: "1px solid var(--surface-border)" }} data-testid="cl-preview">
                {/* Letterhead */}
                {activeTemplate.id === "creative" ? (
                  <div style={{ background: activeTemplate.headerBg, color: activeTemplate.headerColor, padding: "20px 24px" }}>
                    <h2 style={activeTemplate.nameStyle}>{name || "Your Name"}</h2>
                    <p style={{ marginTop: 4, fontSize: 11, opacity: 0.85 }}>Cover Letter — {company}</p>
                  </div>
                ) : (
                  <div style={{ padding: "20px 24px 6px" }}>
                    <h2 style={activeTemplate.nameStyle}>{name || "Your Name"}</h2>
                    <p style={{ marginTop: 6, fontSize: 11, color: "var(--text-muted)" }}>{jobTitle && company ? `${jobTitle} — ${company}` : "Cover Letter"}</p>
                  </div>
                )}
                <div style={{ padding: "16px 24px 20px" }}>
                  {paragraphs.map((p, idx) => (
                    <div key={idx} className="group relative mb-3">
                      <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: "var(--text-primary)", fontFamily: "Georgia, serif" }}>{p}</p>
                      <button onClick={() => handleImproveParagraph(idx)} disabled={improving !== null} className="absolute -top-1 right-0 opacity-0 group-hover:opacity-100 transition-opacity text-[10px] flex items-center gap-1 px-2 py-1 rounded-md" style={{ background: "var(--rolex-gold-light)", color: "var(--rolex-gold)" }} data-testid={`cl-improve-${idx}`}>
                        {improving === idx ? <span className="w-3 h-3 border-2 border-[var(--rolex-gold)] border-t-transparent rounded-full animate-spin" /> : <>{isPaid ? <Sparkles size={10} /> : <Lock size={10} />} {isPaid ? "Improve" : "Improve (Pro)"}</>}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : result?.error ? (
              <p className="text-sm p-4 rounded-xl" style={{ background: "var(--error-bg)", color: "var(--error)" }}>{result.message}</p>
            ) : (
              <div className="flex flex-col items-center justify-center py-16">
                <FileText size={36} style={{ color: "var(--surface-border-mid)" }} />
                <p className="text-sm mt-3" style={{ color: "var(--text-light)" }}>Your cover letter will appear here</p>
                <p className="text-[11px] mt-1 text-center max-w-xs" style={{ color: "var(--text-light)" }}>Hover over any paragraph to use the AI Improve feature.</p>
              </div>
            )}
            {result && !result.error && (
              <div className="flex items-center gap-3 mt-3 pt-3" style={{ borderTop: "1px solid var(--surface-border)" }}>
                <span className="text-[10px]" style={{ color: "var(--text-light)" }}>{(letterText.split(/\s+/).filter(Boolean).length)} words</span>
                {result.uses_remaining !== "unlimited" && <span className="text-[10px]" style={{ color: "var(--rolex-gold)" }}>{result.uses_remaining} free uses remaining</span>}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
